<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import type { UserType } from "@core/composables"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            userType: {
                particular: "Particulier",
                professional: "Professionnel",
            },
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
interface Props {
    /**
     * Type d'utilisateur sélectionné (v-model)
     */
    modelValue: UserType
    /**
     * Options de type disponibles (filtrées)
     */
    allowedTypes?: Array<{ label: string; value: UserType }>
}

interface Emits {
    /**
     * Émis lors du changement de type
     */
    (e: "update:modelValue", value: UserType): void
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    allowedTypes: undefined,
})
const emit = defineEmits<Emits>()

// ================================
// DONNÉES CALCULÉES
// ================================

/**
 * Options pour le sélecteur de type utilisateur
 * Utilise allowedTypes si fourni, sinon toutes les options
 */
const getOptions = computed(() => {
    if (props.allowedTypes) {
        return props.allowedTypes
    }
    return [
        { label: t("userType.particular"), value: "particular" },
        { label: t("userType.professional"), value: "professional" },
    ]
})

// ================================
// INTERACTIONS
// ================================

/**
 * Gère le changement de type d'utilisateur
 * @param value - Nouveau type sélectionné
 */
function onChangeType(value: UserType): void {
    emit("update:modelValue", value)
}
</script>

<template>
    <div class="owner-user-type-selector flex justify-center">
        <!-- ================================ -->
        <!-- SÉLECTEUR DE TYPE UTILISATEUR -->
        <!-- ================================ -->
        <BaseSelectButton
            :model-value="modelValue"
            :options="getOptions"
            option-label="label"
            option-value="value"
            :allow-empty="false"
            @update:model-value="onChangeType"
        />
    </div>
</template>
